import React from "react";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import colors from "../../../assets/theme/colors";

const getUploadLabel = (category, title) => {
  const normalizedTitle = String(title || "").toLowerCase();
  if (category === "behavioral") return "Add support document";
  if (normalizedTitle.includes("medication")) return "Add medication document";
  if (normalizedTitle.includes("care")) return "Add care document";
  return "Add document";
};

const getDocumentName = (document = {}, index) => (
  document.name || document.title || document.url || `Document ${index + 1}`
);

const ProfileDocumentDropZone = ({
  category,
  title,
  helperText,
  documents = [],
  inputRef,
  onUpload,
  onRemove,
}) => {
  const safeDocuments = Array.isArray(documents) ? documents : [];

  return (
    <Stack spacing={1}>
      <Box
        component="label"
        sx={{
          display: "block",
          p: 1.8,
          borderRadius: 2.4,
          border: "1.25px dashed",
          borderColor: colors.landing.borderLight,
          bgcolor: colors.landing.surface,
          cursor: "pointer",
          transition: "all 0.2s ease",
          "&:hover": {
            borderColor: colors.brand.ink,
            bgcolor: colors.landing.sageLight,
          },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          hidden
          accept="image/*,application/pdf"
          multiple
          onChange={(event) => onUpload?.(category, event)}
        />
        <Stack spacing={0.85} alignItems="flex-start">
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexWrap: "wrap" }}>
            <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: colors.semantic.profileFlow.completionText }} />
            <Typography sx={{ fontWeight: 800, color: colors.landing.heroText, fontSize: "0.88rem", lineHeight: 1.2 }}>
              {title}
            </Typography>
            <Typography sx={{ fontSize: "0.68rem", color: colors.semantic.ink2, fontWeight: 700 }}>
              Optional
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontSize: "0.78rem", lineHeight: 1.45, color: colors.semantic.ink2 }}>
            {helperText}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.82rem", color: colors.brand.deep }}>
            {getUploadLabel(category, title)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.72rem", lineHeight: 1.35 }}>
            Drop files here, or tap to browse.
          </Typography>
        </Stack>
      </Box>

      {safeDocuments.length ? (
        <Stack spacing={0.75}>
          {safeDocuments.map((document, index) => {
            const documentName = getDocumentName(document, index);
            const documentUrl = document.url || "";

            return (
              <Box
                key={`${documentUrl || documentName}-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "auto minmax(0, 1fr) auto auto",
                  alignItems: "center",
                  gap: 0.8,
                  p: "9px 10px",
                  borderRadius: 2,
                  border: `1px solid ${colors.semantic.profileFlow.cardBorder}`,
                  bgcolor: colors.semantic.profileFlow.nudgeBg,
                }}
              >
                <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: colors.semantic.profileFlow.completionText }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: colors.landing.heroText, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {documentName}
                  </Typography>
                  {document.uploadedAt ? (
                    <Typography sx={{ mt: 0.1, fontSize: "0.68rem", color: colors.semantic.ink2 }}>
                      Attached
                    </Typography>
                  ) : null}
                </Box>
                <Button
                  component="a"
                  href={documentUrl || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  disabled={!documentUrl}
                  startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 14 }} />}
                  sx={{
                    minWidth: 0,
                    px: 0.7,
                    py: 0.25,
                    borderRadius: 999,
                    textTransform: "none",
                    fontSize: "0.72rem",
                    fontWeight: 800,
                    color: colors.semantic.profileFlow.completionText,
                  }}
                >
                  Open
                </Button>
                <IconButton
                  aria-label={`Remove ${documentName}`}
                  onClick={() => onRemove?.(category, index)}
                  size="small"
                  sx={{
                    width: 28,
                    height: 28,
                    color: colors.semantic.ink2,
                    bgcolor: colors.semantic.profileFlow.cardBg,
                    "&:hover": {
                      bgcolor: colors.semantic.profileFlow.notStartedBg,
                    },
                  }}
                >
                  <CloseRoundedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            );
          })}
        </Stack>
      ) : null}
    </Stack>
  );
};

export default ProfileDocumentDropZone;
